import { useTranslation } from 'react-i18next';
import createGridCustomCells from '@components-clean/core/grid/custom-cell';
import { YesNoCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { FieldDefinitionDto, RowDataSourceDto } from '@interfaces-clean/field-rdl-definition.interface';
import {
    commonCreatedUpdatedColumns,
    commonHiddenColumns,
    commonVisibleColumns,
} from '@pages-clean/admin/study/manage-panels/columns/column-list';
import { PanelGrid } from '@pages-clean/admin/study/manage-panels/columns/panel-grid-interface';
import { OptionsProportionsOT } from '@pages-clean/admin/study/manage-panels/tab-content/panel-details/types/proportionsOT/interfaces';
import {
    AutomationRange,
    ControlHubModelPeriodType,
    ModelPlotBy,
    ModelPlotCalculation,
    ModelScaleBy,
    PanelType,
    ProportionsOTBenchmarkType,
    ProportionsOTCountryBenchmarkType,
    ProportionsOTThresholdType,
} from 'types/graphql-global-types';
import { getPanels_OpraQueries_panels_Panel } from '@queries/generated/central-monitoring.generated';

class ProportionsOTGrid implements PanelGrid {
    // TODO: investigate not using the hook here
    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/rules-of-hooks
    private t = useTranslation(['admin'], { keyPrefix: 'managePanels' }).t;

    // TODO: investigate not using the hook here
    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/rules-of-hooks
    private tOptionsProportionsOT = useTranslation('admin', {
        keyPrefix: 'managePanels.panelDetails.proportionsOT.options',
    }).t;

    private cm2Fields: FieldDefinitionDto[] | null | undefined;

    private cm2DataSources: RowDataSourceDto[] | null | undefined;

    private ScaleByOptions: { id: ModelScaleBy; name: string }[];

    private EligibilityPeriodOptions: { id: ControlHubModelPeriodType; name: string }[];

    private PlotByOptions: { id: ModelPlotBy; name: string }[];

    private ThresholdTypeOptions: { id: ProportionsOTThresholdType; name: string }[];

    private BenchmarkTypeOptions: { id: ProportionsOTBenchmarkType; name: string }[];

    private CountryBenchmarkTypeOptions: { id: ProportionsOTCountryBenchmarkType; name: string }[];

    private PlotCalculationOptions: { id: ModelPlotCalculation; name: string }[];

    private RangeOptions: { id: AutomationRange; name: string }[];

    private TooltipCell = createGridCustomCells().TooltipCell;

    constructor(
        cm2Fields: FieldDefinitionDto[] | null | undefined,
        cm2DataSources: RowDataSourceDto[] | null | undefined,
    ) {
        this.cm2Fields = cm2Fields;
        this.cm2DataSources = cm2DataSources;

        ({
            ScaleByOptions: this.ScaleByOptions,
            EligibilityPeriodOptions: this.EligibilityPeriodOptions,
            PlotByOptions: this.PlotByOptions,
            ThresholdTypeOptions: this.ThresholdTypeOptions,
            BenchmarkTypeOptions: this.BenchmarkTypeOptions,
            CountryBenchmarkTypeOptions: this.CountryBenchmarkTypeOptions,
            PlotCalculationOptions: this.PlotCalculationOptions,
            RangeOptions: this.RangeOptions,
        } = OptionsProportionsOT(this.tOptionsProportionsOT));
    }

    GridTitle(): string {
        return this.t('grid.proportionsOT.title');
    }

    // eslint-disable-next-line class-methods-use-this
    ExportName(): string {
        return 'Proportions Over Time';
    }

    PanelData(panels: (getPanels_OpraQueries_panels_Panel | null)[] | null | undefined): never[] | undefined {
        const proportionsOTPanels = panels?.filter((panel) => panel?.panelType === PanelType.PROPORTIONS_OVER_TIME);

        // @ts-ignore
        return proportionsOTPanels
            ?.map((panel) => ({
                ...panel,
                updatedByFullName: `${panel?.updatedBy?.firstName} ${panel?.updatedBy?.lastName}`,
                createdByFullName: `${panel?.createdBy?.firstName} ${panel?.createdBy?.lastName}`,
                active: panel?.active || false,
                name: panel?.name || '',
                rowData: this.cm2DataSources?.find(
                    (field) => field?.id === panel?.proportionsOverTimeParameters?.rowData,
                )?.name,
                rowDataDate: this.cm2Fields?.find(
                    (field) => field?.key === panel?.proportionsOverTimeParameters?.rowDataDate,
                )?.name,
                rowDataFlag: this.cm2Fields?.find(
                    (field) => field?.key === panel?.proportionsOverTimeParameters?.rowDataFlag,
                )?.name,
                qtl: panel?.proportionsOverTimeParameters?.qtl || false,
                reportOutOfTolerance: panel?.proportionsOverTimeParameters?.reportOutOfTolerance || false,
                scaleBy: this.ScaleByOptions.find((sb) => sb.id === panel?.proportionsOverTimeParameters?.scaleBy)
                    ?.name,
                eligibilityPeriod: this.EligibilityPeriodOptions.find(
                    (ep) => ep.id === panel?.proportionsOverTimeParameters?.eligibilityPeriod,
                )?.name,
                plotBy: this.PlotByOptions.find((pb) => pb.id === panel?.proportionsOverTimeParameters?.plotBy)?.name,
                thresholdType: this.ThresholdTypeOptions.find(
                    (tt) => tt.id === panel?.proportionsOverTimeParameters?.thresholdType,
                )?.name,
                benchmarkType: this.BenchmarkTypeOptions.find(
                    (bt) => bt.id === panel?.proportionsOverTimeParameters?.benchmarkType,
                )?.name,
                countryBenchmark: this.CountryBenchmarkTypeOptions.find(
                    (cb) => cb.id === panel?.proportionsOverTimeParameters?.countryBenchmark,
                )?.name,
                cumulativeOrTimePeriod: this.PlotCalculationOptions.find(
                    (pc) => pc.id === panel?.proportionsOverTimeParameters?.cumulativeOrTimePeriod,
                )?.name,
                numberOfTimePeriods: panel?.proportionsOverTimeParameters?.numberOfTimePeriods,
                automationRange: this.RangeOptions.find(
                    (pc) => pc.id === panel?.proportionsOverTimeParameters?.automationRange,
                )?.name,
            }))
            .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
    }

    Columns(): GridColumnProps[] {
        return [
            ...commonVisibleColumns(this.t, 'proportionsOverTimeParameters'),
            {
                field: 'qtl',
                title: this.tOptionsProportionsOT('panel-qtl'),
                id: 'panel-qtl',
                cells: { data: YesNoCell },
                filter: 'boolean',
                minWidth: 95,
            },
            {
                field: 'reportOutOfTolerance',
                title: this.tOptionsProportionsOT('panel-reportoot'),
                id: 'panel-reportoot',
                cells: { data: YesNoCell },
                filter: 'boolean',
                minWidth: 95,
            },
            {
                field: 'rowData',
                title: this.tOptionsProportionsOT('panel-RowData'),
                id: 'panel-RowData',
                filter: 'text',
                minWidth: 210,
                cells: { data: this.TooltipCell({ isCsv: false }) },
            },
            {
                field: 'rowDataDate',
                title: this.tOptionsProportionsOT('panel-RowDataDateField'),
                id: 'panel-RowDataDateField',
                filter: 'text',
                minWidth: 210,
                cells: { data: this.TooltipCell({ isCsv: false }) },
            },
            {
                field: 'rowDataFlag',
                title: this.tOptionsProportionsOT('panel-RowDataFlagField'),
                id: 'panel-RowDataFlagField',
                filter: 'text',
                minWidth: 210,
                cells: { data: this.TooltipCell({ isCsv: false }) },
            },

            {
                field: 'scaleBy',
                title: this.tOptionsProportionsOT('panel-scaleby'),
                minWidth: 80,
                id: 'panel-scaleby',
                filter: 'text',
                lovData: [
                    this.tOptionsProportionsOT(ModelScaleBy._1),
                    this.tOptionsProportionsOT(ModelScaleBy._10),
                    this.tOptionsProportionsOT(ModelScaleBy._100),
                    this.tOptionsProportionsOT(ModelScaleBy._1000),
                ],
            },
            {
                field: 'plotBy',
                title: this.tOptionsProportionsOT('panel-plotby'),
                minWidth: 100,
                id: 'panel-plotby',
                filter: 'text',
                lovData: [
                    this.tOptionsProportionsOT(ModelPlotBy.WEEKS),
                    this.tOptionsProportionsOT(ModelPlotBy.MONTHS),
                ],
            },
            {
                field: 'cumulativeOrTimePeriod',
                title: this.tOptionsProportionsOT('panel-plotcalculation'),
                minWidth: 140,
                id: 'panel-plotcalculation',
                filter: 'text',
                lovData: [
                    this.tOptionsProportionsOT(ModelPlotCalculation.CUMULATIVE),
                    this.tOptionsProportionsOT(ModelPlotCalculation.BY_TIME_PERIOD),
                ],
            },
            {
                title: this.tOptionsProportionsOT('panel-automation'),
                id: 'panel-automation',
                field: 'field-automation',
                children: [
                    {
                        field: 'automationRange',
                        title: this.tOptionsProportionsOT('panel-automationRange'),
                        id: 'panel-automationRange',
                        filter: 'text',
                        minWidth: 135,
                        lovData: [
                            this.tOptionsProportionsOT(AutomationRange.ALL),
                            this.tOptionsProportionsOT(AutomationRange.SPECIFIC_RANGE),
                        ],
                    },
                    {
                        field: 'numberOfTimePeriods',
                        title: this.tOptionsProportionsOT('panel-numberOfTimePeriods'),
                        id: 'panel-numberOfTimePeriods',
                        minWidth: 100,
                        filter: 'numeric',
                    },
                ],
            },
            {
                field: 'proportionsOverTimeParameters.minData',
                title: this.tOptionsProportionsOT('panel-mindata'),
                id: 'panel-mindata',
                minWidth: 70,
                filter: 'numeric',
            },
            {
                title: this.tOptionsProportionsOT('panel-thresholds'),
                id: 'panel-thresholds',
                field: 'field-threshold',
                children: [
                    {
                        field: 'thresholdType',
                        title: 'Type',
                        minWidth: 170,
                        id: 'panel-thresholdtype',
                        filter: 'text',
                        lovData: [
                            this.tOptionsProportionsOT(ProportionsOTThresholdType.FIXED),
                            this.tOptionsProportionsOT(ProportionsOTThresholdType.LANEY_P_CHART),
                            this.tOptionsProportionsOT(ProportionsOTThresholdType.P_CHART),
                            this.tOptionsProportionsOT(ProportionsOTThresholdType.QUASI_BINOMIAL_P_CHART),
                        ],
                    },
                    {
                        field: 'proportionsOverTimeParameters.upperAmber',
                        title: this.tOptionsProportionsOT('panel-upperAmberThreshold'),
                        minWidth: 100,
                        filter: 'numeric',
                        id: 'panel-upperAmberThreshold',
                    },
                    {
                        field: 'proportionsOverTimeParameters.upperRed',
                        title: this.tOptionsProportionsOT('panel-upperRedThreshold'),
                        minWidth: 100,
                        filter: 'numeric',
                        id: 'panel-upperRedThreshold',
                    },
                    {
                        field: 'proportionsOverTimeParameters.lowerAmber',
                        title: this.tOptionsProportionsOT('panel-lowerAmberThreshold'),
                        minWidth: 100,
                        filter: 'numeric',
                        id: 'panel-lowerAmberThreshold',
                    },
                    {
                        field: 'proportionsOverTimeParameters.lowerRed',
                        title: this.tOptionsProportionsOT('panel-lowerRedThreshold'),
                        minWidth: 100,
                        filter: 'numeric',
                        id: 'panel-lowerRedThreshold',
                    },
                ],
            },
            {
                title: 'Benchmark',
                id: 'panel-benchmark',
                field: 'field-benchmark',
                children: [
                    {
                        field: 'benchmarkType',
                        title: this.tOptionsProportionsOT('panel-benchmarktype'),
                        minWidth: 140,
                        id: 'panel-benchmarktype',
                        filter: 'text',
                        lovData: [
                            this.tOptionsProportionsOT(ProportionsOTBenchmarkType.NONE),
                            this.tOptionsProportionsOT(ProportionsOTBenchmarkType.STUDY_SPECIFIC),
                            this.tOptionsProportionsOT(ProportionsOTBenchmarkType.STUDY_RATE),
                        ],
                    },
                    {
                        field: 'proportionsOverTimeParameters.studySpecificBenchmark',
                        title: this.tOptionsProportionsOT('panel-benchmarkstudyspecificvalue'),
                        minWidth: 100,
                        filter: 'numeric',
                        id: 'panel-benchmarkstudyspecificvalue',
                    },
                    {
                        field: 'countryBenchmark',
                        title: this.tOptionsProportionsOT('panel-countrybenchmark'),
                        minWidth: 140,
                        filter: 'text',
                        id: 'panel-countrybenchmark',
                        lovData: [
                            this.tOptionsProportionsOT(ProportionsOTCountryBenchmarkType.COUNTRY_RATE),
                            this.tOptionsProportionsOT(ProportionsOTCountryBenchmarkType.NONE),
                            this.tOptionsProportionsOT(ProportionsOTCountryBenchmarkType.STUDY_SPECIFIC),
                            this.tOptionsProportionsOT(ProportionsOTCountryBenchmarkType.STUDY_RATE),
                        ],
                    },
                ],
            },
            {
                title: this.tOptionsProportionsOT('panel-eligibility'),
                id: 'panel-eligibility',
                field: 'field-eligability',
                children: [
                    {
                        field: 'proportionsOverTimeParameters.eligibilityUnit',
                        title: this.tOptionsProportionsOT('panel-eligibilityunit'),
                        minWidth: 70,
                        id: 'panel-eligibilityunit',
                        filter: 'numeric',
                    },
                    {
                        field: 'eligibilityPeriod',
                        title: this.tOptionsProportionsOT('panel-eligibilityperiod'),
                        minWidth: 100,
                        filter: 'text',
                        id: 'panel-eligibilityperiod',
                        lovData: [
                            this.tOptionsProportionsOT(ControlHubModelPeriodType.DAYS),
                            this.tOptionsProportionsOT(ControlHubModelPeriodType.WEEKS),
                            this.tOptionsProportionsOT(ControlHubModelPeriodType.MONTHS),
                            this.tOptionsProportionsOT(ControlHubModelPeriodType.QUARTERS),
                            this.tOptionsProportionsOT(ControlHubModelPeriodType.YEARS),
                        ],
                    },
                ],
            },
            {
                field: 'proportionsOverTimeParameters.expectedVolume',
                title: this.tOptionsProportionsOT('panel-expectedvolume'),
                id: 'panel-expectedvolume',
                minWidth: 70,
                filter: 'numeric',
            },

            {
                field: 'proportionsOverTimeParameters.xAxisPtsToShade',
                title: this.tOptionsProportionsOT('panel-xaxisptstoshade'),
                id: 'panel-xaxisptstoshade',
                minWidth: 70,
                filter: 'numeric',
            },
            {
                field: 'proportionsOverTimeParameters.numeratorName',
                title: this.tOptionsProportionsOT('panel-NumeratorField'),
                id: 'panel-NumeratorField',
                filter: 'text',
                minWidth: 210,
                cells: { data: this.TooltipCell({ isCsv: false }) },
            },
            {
                field: 'proportionsOverTimeParameters.denominatorName',
                title: this.tOptionsProportionsOT('panel-DenominatorField'),
                id: 'panel-DenominatorField',
                filter: 'text',
                minWidth: 210,
                cells: { data: this.TooltipCell({ isCsv: false }) },
            },
            ...commonCreatedUpdatedColumns(this.t),
            ...commonHiddenColumns(this.t),
        ];
    }
}

export default ProportionsOTGrid;
